/**
 * Light/dark theme primitives — pure, framework-free helpers shared by the
 * ThemeProvider, the FOUC bootstrap script (index.html mirrors this logic), and
 * tests.
 *
 * Strategy: class-based. A resolved theme of "dark" puts the `.dark` class on
 * <html> (which the authored `.dark {}` token block in index.css keys off) and
 * sets `color-scheme`. Preference "system" tracks prefers-color-scheme.
 */

export type ThemePreference = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

/** localStorage key. Kept in sync with the inline bootstrap in index.html. */
export const THEME_STORAGE_KEY = 'agileco-theme';

export const THEME_PREFERENCES: ThemePreference[] = ['light', 'dark', 'system'];

function isThemePreference(value: unknown): value is ThemePreference {
  return value === 'light' || value === 'dark' || value === 'system';
}

/** Read the saved preference, defaulting to 'system' when unset/invalid. */
export function readStoredPreference(): ThemePreference {
  if (typeof window === 'undefined') return 'system';
  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    return isThemePreference(stored) ? stored : 'system';
  } catch {
    return 'system';
  }
}

/** Persist the preference (best-effort — storage can throw in private mode). */
export function writeStoredPreference(preference: ThemePreference): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, preference);
  } catch {
    /* ignore */
  }
}

/** Current OS-level preference. */
export function getSystemTheme(): ResolvedTheme {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return 'light';
  }
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

/** Collapse a preference to the concrete theme that should be applied. */
export function resolveTheme(
  preference: ThemePreference,
  systemTheme: ResolvedTheme = getSystemTheme(),
): ResolvedTheme {
  return preference === 'system' ? systemTheme : preference;
}

/** Apply a resolved theme to <html>: toggle `.dark` and set color-scheme. */
export function applyTheme(resolved: ResolvedTheme): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.classList.toggle('dark', resolved === 'dark');
  root.style.colorScheme = resolved;
}
